/* eslint-disable @typescript-eslint/no-explicit-any */
import { Icon } from '@gerdau/hefesto/Icon';
import alasql from 'alasql';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import theme from '../../../../assets/styles/theme/theme';
import {
  IBilledByDayData,
  IBilledByDayProduct,
  IBilledByDayState
} from '../../../../store/ducks/dashboard/types';
import { sendTagging } from '../../../../store/ducks/tagging/actions';
import { ITaggingParams } from '../../../../store/ducks/tagging/types';
import { DashboardItemContainer } from '../../styles';
import BilledByDayChart from './BilledByDayChart';
import BilledByDayItemHeader from './BilledByDayItemHeader';
import BilledByDayTable from './BilledByDayTable';
import { Modal } from '@gerdau/hefesto/Modal';

interface IBilledByDayProps {
  billedByDayState: IBilledByDayState;
  errorMessageBlock: React.ReactNode;
  onChangeMonth(selectedMonth: moment.Moment): void;
}

interface totalRow {
  'Tipo de produto': string;
  Total: number;
}

const BilledByDay: React.FC<IBilledByDayProps> = ({
  billedByDayState,
  errorMessageBlock,
  onChangeMonth,
  children
}) => {
  const title = 'Volume faturado por dia (t)';
  const [canShowModalTable, setCanShowModalTable] = useState(false);
  const [allProductsDescription, setAllProductsDescription] = useState<
    string[]
  >([]);

  const dispatch = useDispatch();

  const prepareAndSendTaggingGenerateExcel = () => {
    const taggingParamsGenerateExcel: ITaggingParams = {
      screenName: 'LG:PainelGestao:Selecao',
      itemName: 'button:baixar',
      itemDescription: 'painelGestao_volumeFaturadoPorDia_baixar',
      event: 'click'
    };
    dispatch(sendTagging(taggingParamsGenerateExcel));
  };

  const prepareAndSendTaggingModalTable = () => {
    const taggingParamsModalTable: ITaggingParams = {
      screenName: 'LG:PainelGestao:Selecao',
      itemName: 'button:verComoTabela',
      itemDescription: 'painelGestao_volumeFaturadoPorDia_verComoTabela',
      event: 'click'
    };
    dispatch(sendTagging(taggingParamsModalTable));
  };

  useEffect(() => {
    const getAllProductsDescription = (): string[] => {
      const productsDescription: string[] = [];
      billedByDayState.data.forEach((item: IBilledByDayData) => {
        item.products.forEach((product: IBilledByDayProduct) => {
          if (!productsDescription.includes(product.divisionDescription)) {
            productsDescription.push(product.divisionDescription);
          }
        });
      });
      productsDescription.sort((a: string, b: string) =>
        a < b ? -1 : a > b ? 1 : 0
      );
      return productsDescription;
    };
    setAllProductsDescription(getAllProductsDescription());
  }, [billedByDayState.data]);

  const isEmptyData = (): boolean => {
    return !billedByDayState.data.some(
      (item: IBilledByDayData) => item.products.length > 0
    );
  };

  const generateBilledByDayExcel = () => {
    const dataToExcel: totalRow[] = [];

    const chartData = billedByDayState.data;

    let totalsRow: totalRow = {
      'Tipo de produto': 'Total diário',
      Total: chartData.reduce(
        (acc: number, curr: IBilledByDayData) =>
          curr.products.reduce(
            (accProductsSum: number, currProduct: IBilledByDayProduct) =>
              currProduct.billedToday + accProductsSum,
            0
          ) + acc,
        0
      )
    };
    chartData.forEach((item: IBilledByDayData) => {
      totalsRow = {
        ...totalsRow,
        [item.date]: item.products.reduce(
          (accProductsSum: number, currProduct: IBilledByDayProduct) =>
            currProduct.billedToday + accProductsSum,
          0
        )
      };
    });
    dataToExcel.push(totalsRow);

    allProductsDescription.forEach((productDescription) => {
      let row = {
        'Tipo de produto': productDescription,
        Total: chartData.reduce(
          (acc: number, curr: IBilledByDayData) =>
            acc +
            (curr.products.find(
              (i: IBilledByDayProduct) =>
                i.divisionDescription === productDescription
            )?.billedToday || 0),
          0
        )
      };

      chartData.forEach((item: IBilledByDayData) => {
        row = {
          ...row,
          [item.date]:
            item.products.find(
              (i: IBilledByDayProduct) =>
                i.divisionDescription === productDescription
            )?.billedToday || 0
        };
      });

      dataToExcel.push(row);
    });

    const opts = {
      headers: true,
      column: {
        style: {
          width: 200,
          Font: { Bold: '1' },
          Interior: { Color: theme.colors.waterBlue, Pattern: 'Solid' },
          Alignment: { Horizontal: 'Right' }
        }
      },
      row: {
        style: {
          Font: { Color: theme.colors.black },
          Alignment: { Horizontal: 'Right' }
        }
      },
      columns: [
        { width: 100, height: 100, columnid: 'Tipo de produto' },
        { width: 100, height: 100, columnid: 'Total' }
      ]
    };

    chartData.forEach((item: IBilledByDayData) => {
      opts.columns.push({
        width: 100,
        height: 100,
        columnid: item.date
      });
    });

    const dataMonth = moment(chartData[0].date, 'DD/MM').format('MMMM');
    const fileName = `faturados_${dataMonth}`;

    alasql('SELECT * INTO XLSXML("' + fileName + '.xls",?) FROM ?', [
      opts,
      dataToExcel
    ]);
  };

  const prepareAndSendTagging = (itemName: string, itemDescription: string) => {
    dispatch(
      sendTagging({
        screenName: 'LG:PainelGestao:Selecao',
        itemName: itemName,
        itemDescription: itemDescription,
        event: 'click'
      })
    );
  };

  return (
    <DashboardItemContainer>
      <BilledByDayItemHeader
        title={title}
        selectedMonth={billedByDayState.monthDate}
        onSelectMonth={onChangeMonth}
        loading={billedByDayState.loading}
        actions={
          !billedByDayState.error && !isEmptyData()
            ? [
                {
                  description: 'Baixar',
                  icon: <Icon option="Download" />,
                  onClickAction: () => {
                    prepareAndSendTaggingGenerateExcel();
                    generateBilledByDayExcel();
                  }
                },
                {
                  description: 'Ver como tabela',
                  onClickAction: () => {
                    prepareAndSendTaggingModalTable();
                    setCanShowModalTable(true);
                  }
                }
              ]
            : []
        }
      />

      {billedByDayState.error && !billedByDayState.loading && (
        <>{errorMessageBlock}</>
      )}

      {billedByDayState.loading && <>{children}</>}

      {!billedByDayState.error && !billedByDayState.loading && (
        <>
          <BilledByDayChart
            data={billedByDayState.data}
            allProductsDescription={allProductsDescription}
            isEmpty={isEmptyData()}
          />

          {canShowModalTable && (
            <Modal
              title={title}
              onClose={() => setCanShowModalTable(false)}
              buttons={{
                right: {
                  children: 'Baixar',
                  name: 'baixar',
                  onClick: () => {
                    generateBilledByDayExcel();
                    prepareAndSendTagging(
                      'button:baixar',
                      'painelGestao_VolumeFaturadoPorDia_baixar'
                    );
                  },
                  type: 'button'
                }
              }}
            >
              <>
                <BilledByDayTable
                  data={billedByDayState.data}
                  allProductsDescription={allProductsDescription}
                />
              </>
            </Modal>
          )}
        </>
      )}
    </DashboardItemContainer>
  );
};

export default BilledByDay;
